import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAssetsStore } from '@/stores/assetsStore';
import { NodeBase, Domain, Purdue } from '@/types/assets';
import { toast } from 'sonner';

interface NodeEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string | null;
}

const nodeTypes: Array<{ value: NodeBase['type']; label: string }> = [
  { value: 'site', label: 'Site' },
  { value: 'zone', label: 'Zone' },
  { value: 'system', label: 'System' },
  { value: 'endpoint', label: 'Endpoint' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'shared_infrastructure', label: 'Shared Infrastructure' },
];

type NodeMeta = NonNullable<NodeBase['meta']>;
type OperatingHours = NonNullable<NodeMeta['operating_hours']>;

interface NodeFormState {
  label?: string;
  type?: NodeBase['type'];
  domain?: Domain;
  purdue_level?: Purdue;
  description?: string;
  ip?: string;
  os?: string;
  model?: string;
  vendor?: string;
  fw_version?: string;
  ports?: string;
  protocol?: string;
  power_rating?: string;
  location?: string;
  site_code?: string;
  address?: string;
  country?: string;
  timezone?: string;
  site_criticality?: 'Low' | 'Medium' | 'High';
  physical_security?: 'Basic' | 'Controlled' | 'Restricted';
  operating_hours?: OperatingHours;
}

const emptyOperatingHours: OperatingHours = {
  start_time: '',
  end_time: '',
  weekdays: [],
};

const businessHours: OperatingHours = {
  start_time: '07:00',
  end_time: '16:00',
  weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
};

const fullTimeHours: OperatingHours = {
  start_time: '00:00',
  end_time: '23:59',
  weekdays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
};

const getFieldsForType = (type: string) => {
  const baseFields = ['label', 'type'];
  
  switch (type) {
    case 'site':
      return [...baseFields, 'site_code', 'address', 'country', 'timezone', 'site_criticality', 'physical_security', 'operating_hours', 'description'];
    case 'zone':
      return [...baseFields, 'domain', 'purdue_level', 'description'];
    case 'conduit':
      return [...baseFields, 'domain', 'purdue_level', 'protocol', 'description'];
    case 'workstation':
    case 'server':
      return [...baseFields, 'domain', 'ip', 'os', 'model', 'vendor', 'description'];
    case 'plc':
    case 'hmi':
      return [...baseFields, 'domain', 'purdue_level', 'ip', 'model', 'vendor', 'fw_version', 'description'];
    case 'switch':
    case 'router':
    case 'firewall':
      return [...baseFields, 'domain', 'purdue_level', 'ip', 'model', 'vendor', 'fw_version', 'ports', 'description'];
    case 'sensor':
    case 'actuator':
      return [...baseFields, 'domain', 'purdue_level', 'model', 'vendor', 'protocol', 'description'];
    case 'drive':
    case 'motor':
    case 'pump':
    case 'valve':
      return [...baseFields, 'domain', 'purdue_level', 'model', 'vendor', 'power_rating', 'description'];
    default:
      return [...baseFields, 'domain', 'description'];
  }
};

export function NodeEditDialog({ open, onOpenChange, nodeId }: NodeEditDialogProps) {
  const { nodes, updateNode } = useAssetsStore();
  const [formData, setFormData] = useState<NodeFormState>({});
  const [loading, setLoading] = useState(false);

  const currentNode = nodes.find(n => n.id === nodeId);

  const setOperatingHours = (updater: (current: OperatingHours) => OperatingHours) => {
    setFormData(prev => {
      const current: OperatingHours = prev.operating_hours ?? emptyOperatingHours;
      const next = updater({ ...current });
      return {
        ...prev,
        operating_hours: { ...next },
      };
    });
  };

  useEffect(() => {
    if (currentNode) {
      const meta: Partial<NodeMeta> = currentNode.data.meta ?? {};
      setFormData({
        label: currentNode.data.label,
        type: currentNode.data.type,
        domain: currentNode.data.domain,
        purdue_level: currentNode.data.purdue_level,
        description: meta.description ?? '',
        ip: meta.ip ?? '',
        os: meta.os ?? '',
        model: meta.model ?? '',
        vendor: meta.vendor ?? '',
        fw_version: meta.fw_version ?? '',
        ports: meta.ports ?? '',
        protocol: meta.protocol ?? '',
        power_rating: meta.power_rating ?? '',
        location: meta.location ?? '',
        site_code: meta.site_code ?? '',
        address: meta.address ?? '',
        country: meta.country ?? '',
        timezone: meta.timezone ?? '',
        site_criticality: meta.site_criticality ?? undefined,
        physical_security: meta.physical_security ?? undefined,
        operating_hours: meta.operating_hours ? { ...meta.operating_hours } : { ...emptyOperatingHours },
      });
    }
  }, [currentNode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nodeId || !formData.label || !formData.type || !currentNode) return;

    setLoading(true);
    try {
      const metaUpdates: Partial<NodeMeta> = {
        ...(currentNode.data.meta ?? {}),
        description: formData.description?.trim() || undefined,
        ip: formData.ip?.trim() || undefined,
        os: formData.os?.trim() || undefined,
        model: formData.model?.trim() || undefined,
        vendor: formData.vendor?.trim() || undefined,
        fw_version: formData.fw_version?.trim() || undefined,
        ports: formData.ports?.trim() || undefined,
        protocol: formData.protocol?.trim() || undefined,
        power_rating: formData.power_rating?.trim() || undefined,
        location: formData.location?.trim() || undefined,
        site_code: formData.site_code?.trim() || undefined,
        address: formData.address?.trim() || undefined,
        country: formData.country?.trim() || undefined,
        timezone: formData.timezone?.trim() || undefined,
        site_criticality: formData.site_criticality,
        physical_security: formData.physical_security,
        operating_hours: formData.operating_hours ? { ...formData.operating_hours } : undefined,
      };

      const updates: Partial<NodeBase> = {
        label: formData.label?.trim(),
        type: formData.type,
        domain: formData.domain,
        purdue_level: formData.purdue_level,
        meta: metaUpdates,
      };

      await updateNode(nodeId, updates);
      toast.success('Node updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating node:', error);
      toast.error('Failed to update node');
    } finally {
      setLoading(false);
    }
  };

  if (!currentNode) return null;

  const visibleFields = getFieldsForType(formData.type || currentNode.data.type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Node</DialogTitle>
          <DialogDescription>
            Update the details of this asset node.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {visibleFields.includes('label') && (
            <div className="space-y-2">
              <Label htmlFor="label">Name *</Label>
              <Input
                id="label"
                value={formData.label ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Enter asset name"
                required
              />
            </div>
          )}

          {visibleFields.includes('type') && (
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select 
                value={formData.type ?? ''} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as NodeBase['type'] }))}
                disabled={currentNode.data.type === 'site'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select node type" />
                </SelectTrigger>
                <SelectContent>
                  {nodeTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentNode.data.type === 'site' && (
                <p className="text-sm text-muted-foreground">
                  Site type cannot be changed after creation
                </p>
              )}
            </div>
          )}

          {visibleFields.includes('domain') && (
            <div className="space-y-2">
              <Label htmlFor="domain">Domain</Label>
              <Select 
                value={formData.domain ?? ''} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, domain: value as Domain }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select domain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IT">IT</SelectItem>
                  <SelectItem value="OT">OT</SelectItem>
                  <SelectItem value="Cloud">Cloud</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                  <SelectItem value="IoT">IoT</SelectItem>
                  <SelectItem value="Physical">Physical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {visibleFields.includes('purdue_level') && (
            <div className="space-y-2">
              <Label htmlFor="purdue_level">Purdue Level</Label>
              <Select 
                value={formData.purdue_level ?? ''} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, purdue_level: value as Purdue }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Purdue level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L0">L0 - Process</SelectItem>
                  <SelectItem value="L1">L1 - Basic Control</SelectItem>
                  <SelectItem value="L2">L2 - Supervisory Control</SelectItem>
                  <SelectItem value="L3">L3 - Operations Management</SelectItem>
                  <SelectItem value="L3.5">L3.5 - DMZ</SelectItem>
                  <SelectItem value="L4">L4 - Business Logistics</SelectItem>
                  <SelectItem value="L5">L5 - Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleFields.includes('ip') && (
              <div className="space-y-2">
                <Label htmlFor="ip">IP Address</Label>
                <Input
                  id="ip"
                  value={formData.ip ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip: e.target.value }))}
                  placeholder="192.168.1.100"
                />
              </div>
            )}

            {visibleFields.includes('model') && (
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Device model"
                />
              </div>
            )}

            {visibleFields.includes('vendor') && (
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Input
                  id="vendor"
                  value={formData.vendor ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
                  placeholder="Manufacturer"
                />
              </div>
            )}

            {visibleFields.includes('fw_version') && (
              <div className="space-y-2">
                <Label htmlFor="fw_version">Firmware Version</Label>
                <Input
                  id="fw_version"
                  value={formData.fw_version ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fw_version: e.target.value }))}
                  placeholder="v1.2.3"
                />
              </div>
            )}

            {visibleFields.includes('os') && (
              <div className="space-y-2">
                <Label htmlFor="os">Operating System</Label>
                <Input
                  id="os"
                  value={formData.os ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, os: e.target.value }))}
                  placeholder="Windows 11, Ubuntu 22.04, etc."
                />
              </div>
            )}

            {visibleFields.includes('protocol') && (
              <div className="space-y-2">
                <Label htmlFor="protocol">Protocol</Label>
                <Input
                  id="protocol"
                  value={formData.protocol ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, protocol: e.target.value }))}
                  placeholder="Modbus, EtherNet/IP, etc."
                />
              </div>
            )}

            {visibleFields.includes('ports') && (
              <div className="space-y-2">
                <Label htmlFor="ports">Ports</Label>
                <Input
                  id="ports"
                  value={formData.ports ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ports: e.target.value }))}
                  placeholder="24, 48, etc."
                />
              </div>
            )}

            {visibleFields.includes('power_rating') && (
              <div className="space-y-2">
                <Label htmlFor="power_rating">Power Rating</Label>
                <Input
                  id="power_rating"
                  value={formData.power_rating ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, power_rating: e.target.value }))}
                  placeholder="5kW, 100HP, etc."
                />
              </div>
            )}

            {visibleFields.includes('location') && (
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Building A, Floor 2"
                />
              </div>
            )}

            {visibleFields.includes('site_code') && (
              <div className="space-y-2">
                <Label htmlFor="site_code">Site Code</Label>
                <Input
                  id="site_code"
                  value={formData.site_code ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_code: e.target.value }))}
                  placeholder="e.g., HQ-01"
                  maxLength={8}
                />
              </div>
            )}

            {visibleFields.includes('address') && (
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Street address, City"
                />
              </div>
            )}

            {visibleFields.includes('country') && (
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Country code (e.g., SE, US)"
                />
              </div>
            )}

            {visibleFields.includes('timezone') && (
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Input
                  id="timezone"
                  value={formData.timezone ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                  placeholder="Europe/Stockholm"
                />
              </div>
            )}

            {visibleFields.includes('site_criticality') && (
              <div className="space-y-2">
                <Label htmlFor="site_criticality">Site Criticality</Label>
                <Select 
                  value={formData.site_criticality ?? ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, site_criticality: value as NodeFormState['site_criticality'] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select criticality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {visibleFields.includes('physical_security') && (
              <div className="space-y-2">
                <Label htmlFor="physical_security">Physical Security</Label>
                <Select 
                  value={formData.physical_security ?? ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, physical_security: value as NodeFormState['physical_security'] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select security level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Controlled">Controlled</SelectItem>
                    <SelectItem value="Restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {visibleFields.includes('operating_hours') && (
              <div className="col-span-2 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Operation Hours</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const current: OperatingHours = formData.operating_hours ?? emptyOperatingHours;
                      const isFullTime = current.weekdays.length === 7 &&
                        current.start_time === '00:00' &&
                        current.end_time === '23:59';
                      setFormData(prev => ({
                        ...prev,
                        operating_hours: isFullTime ? { ...businessHours } : { ...fullTimeHours },
                      }));
                    }}
                  >
                    {(formData.operating_hours?.weekdays?.length === 7 &&
                      formData.operating_hours?.start_time === '00:00' &&
                      formData.operating_hours?.end_time === '23:59') ? '✓ ' : ''}24/7 365 Operation
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="operating_start_time" className="text-sm">Start Time</Label>
                    <Input
                      id="operating_start_time"
                      type="time"
                      value={formData.operating_hours?.start_time ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setOperatingHours(current => ({
                          ...current,
                          start_time: value,
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="operating_end_time" className="text-sm">End Time</Label>
                    <Input
                      id="operating_end_time"
                      type="time"
                      value={formData.operating_hours?.end_time ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setOperatingHours(current => ({
                          ...current,
                          end_time: value,
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-sm">Weekdays</Label>
                  <div className="flex gap-2 mt-2">
                    {[
                      { code: 'mon', label: 'Mon' },
                      { code: 'tue', label: 'Tue' },
                      { code: 'wed', label: 'Wed' },
                      { code: 'thu', label: 'Thu' },
                      { code: 'fri', label: 'Fri' },
                      { code: 'sat', label: 'Sat' },
                      { code: 'sun', label: 'Sun' },
                    ].map(day => (
                      <Button
                        key={day.code}
                        type="button"
                        variant={(formData.operating_hours?.weekdays ?? []).includes(day.code) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setOperatingHours(current => {
                            const weekdays = current.weekdays.includes(day.code)
                              ? current.weekdays.filter((d) => d !== day.code)
                              : [...current.weekdays, day.code];
                            return {
                              ...current,
                              weekdays,
                            };
                          });
                        }}
                        className="flex-1"
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {visibleFields.includes('description') && (
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details about this asset"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Node'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}